import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { PublicDirectoryFilters } from '../../../shared/types/publicDirectory';
import { PublicGallery } from './PublicGallery';

// Simple search bar component for public directory (replicating SimpleFilterBar's search functionality)
function PublicSearchBar({ 
  value, 
  onChange, 
  placeholder = "🔍 Search public references...",
  viewMode,
  setViewMode
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  viewMode: 'grid' | 'masonry' | 'list';
  setViewMode: (mode: 'grid' | 'masonry' | 'list') => void;
}) {
  return (
    <form onSubmit={(e) => e.preventDefault()} className="relative">
      <div className="bg-white rounded-lg">
        <div className="relative flex items-center">
          <svg className="absolute left-3 sm:left-6 top-1/2 transform -translate-y-1/2 h-4 sm:h-5 w-4 sm:w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 pl-8 sm:pl-12 pr-16 sm:pr-20 py-2 sm:py-3 outline-none bg-transparent text-gray-900 placeholder-gray-500 font-medium text-sm sm:text-base"
          />
          
          {/* View Mode Toggle */}
          <div className="absolute right-2 sm:right-6 top-1/2 transform -translate-y-1/2 flex items-center gap-0.5 sm:gap-1">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-1.5 sm:p-2 rounded border-2 border-black font-bold transition-colors ${
                viewMode === 'grid' 
                  ? "bg-blue-300 text-black" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              title="Grid view"
              aria-label="Grid view"
            >
              <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('masonry')}
              className={`p-1.5 sm:p-2 rounded border-2 border-black font-bold transition-colors ${
                viewMode === 'masonry' 
                  ? "bg-blue-300 text-black" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              title="Masonry view"
              aria-label="Masonry view"
            >
              <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4zM12 4h8v4h-8V4zM4 12h8v8H4v-8zM14 10h6v10h-6V10z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-1.5 sm:p-2 rounded border-2 border-black font-bold transition-colors ${
                viewMode === 'list' 
                  ? "bg-blue-300 text-black" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              title="List view"
              aria-label="List view"
            >
              <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

// Simplified public filters component for the sidebar
function PublicFilters({ filters, onFiltersChange }: {
  filters: PublicDirectoryFilters;
  onFiltersChange: (filters: PublicDirectoryFilters) => void;
}) {
  const updateFilter = <K extends keyof PublicDirectoryFilters>(
    key: K,
    value: PublicDirectoryFilters[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-4">🔍 Filters</h3>
      </div>

      {/* Type Filter */}
      <div>
        <label htmlFor="type-filter" className="block text-sm font-bold text-gray-800 mb-2">Type</label>
        <select
          id="type-filter"
          value={filters.type || ''}
          onChange={(e) => updateFilter('type', (e.target.value as "screenshot" | "component" | "flow" | "screencast") || undefined)}
          className="select select-bordered w-full shadow-comic-sm border-2 border-gray-800"
        >
          <option value="">All Types</option>
          <option value="screenshot">📸 Screenshot</option>
          <option value="component">🧩 Component</option>
          <option value="flow">📊 Flow</option>
          <option value="screencast">🎬 Screencast</option>
        </select>
      </div>

      {/* Sort By */}
      <div>
        <label htmlFor="sort-filter" className="block text-sm font-bold text-gray-800 mb-2">Sort By</label>
        <select
          id="sort-filter"
          value={filters.sortBy || 'newest'}
          onChange={(e) => updateFilter('sortBy', e.target.value as "newest" | "popular" | "views" | "likes")}
          className="select select-bordered w-full shadow-comic-sm border-2 border-gray-800"
        >
          <option value="newest">🕐 Newest First</option>
          <option value="popular">🔥 Most Popular</option>
          <option value="views">👁 Most Viewed</option>
          <option value="likes">❤️ Most Liked</option>
        </select>
      </div>

      {/* Featured Only */}
      <div className="form-control">
        <label className="label cursor-pointer">
          <span className="label-text font-bold text-gray-800">⭐ Featured Only</span>
          <input
            type="checkbox"
            checked={filters.featured || false}
            onChange={(e) => updateFilter('featured', e.target.checked || undefined)}
            className="checkbox checkbox-primary border-2 border-gray-800"
          />
        </label>
      </div>

      {/* Clear Filters */}
      <button
        type="button"
        onClick={() => onFiltersChange({ sortBy: 'newest' })}
        className="btn btn-outline btn-sm w-full shadow-comic-sm border-2 border-gray-800 text-gray-800 hover:bg-yellow-300"
      >
        🗑 Clear All Filters
      </button>
    </div>
  );
}


export function PublicDirectoryPage() {
  const [filters, setFilters] = useState<PublicDirectoryFilters>({
    sortBy: 'newest'
  });
  const [viewMode, setViewMode] = useState<'grid' | 'masonry' | 'list'>('masonry');

  // Fetch public references from API
  const publicReferences = useQuery(api.publicDirectory.getPublicReferences, {
    filters: {
      type: filters.type,
      featured: filters.featured,
      sortBy: filters.sortBy || 'newest',
      search: filters.search
    },
    limit: 20
  });

  // Fetch featured references
  const featuredReferences = useQuery(api.publicDirectory.getFeaturedReferences, {});

  return (
    <div className="min-h-screen bg-base-100">
      {/* Header */}
      <div className="bg-yellow-300 border-b-2 border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="comic-panel bg-white p-8 mb-6 shadow-comic">
              <h1 className="text-4xl md:text-5xl text-pop-black text-gray-800 mb-4 leading-tight">
                <span className="text-primary text-pop-black">Community</span>
                <br />
                <span className="text-secondary text-pop-black">Reference Directory</span>
              </h1>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
                Discover and explore a curated collection of UI references, components, 
                and design patterns shared by the creative community.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Section */}
      {featuredReferences && featuredReferences.length > 0 && (
        <div className="bg-base-100 border-b-2 border-gray-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">⭐ Featured References</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredReferences.slice(0, 6).map((reference) => (
                <div key={reference._id} className="card bg-yellow-300 shadow-comic border-2 border-gray-800 hover:shadow-comic-lg transition-shadow">
                  <figure className="px-4 pt-4">
                    {reference.imageUrl && (
                      <img
                        src={reference.imageUrl}
                        alt={reference.title}
                        className="rounded-lg max-h-48 w-full object-cover border-2 border-gray-800"
                      />
                    )}
                  </figure>
                  <div className="card-body p-4">
                    <h3 className="card-title text-sm text-gray-900">{reference.title}</h3>
                    <div className="badge badge-secondary badge-sm">{reference.type}</div>
                    <div className="flex gap-2 mt-2">
                      <div className="badge badge-ghost badge-sm text-xs">
                        👁 {reference.publicMetadata?.views || 0}
                      </div>
                      <div className="badge badge-ghost badge-sm text-xs">
                        ❤️ {reference.publicMetadata?.likes || 0}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="cutting-mat-bg-subtle min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Sidebar - Filters */}
            <div className="lg:col-span-1 mb-8 lg:mb-0">
              <div className="sticky top-8">
                <div className="card bg-white shadow-comic border-2 border-gray-800">
                  <div className="card-body">
                    <PublicFilters
                      filters={filters}
                      onFiltersChange={setFilters}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content - References */}
            <div className="lg:col-span-3">
              {/* Search and View Controls - styled like SimpleFilterBar */}
              <div className="card bg-white shadow-comic border-2 border-gray-800 mb-6 p-3 sm:p-4">
                <PublicSearchBar
                  value={filters.search || ''}
                  onChange={(search) => setFilters({ ...filters, search })}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                />
              </div>

              {/* Results Summary */}
              {publicReferences && (
                <div className="text-sm text-gray-600 mb-4">
                  Showing {publicReferences.references.length} of {publicReferences.totalCount} public references
                </div>
              )}

              {/* Gallery */}
              <PublicGallery
                references={(publicReferences?.references || []).map(ref => ({
                  ...ref,
                  tags: ref.tags?.filter((tag): tag is NonNullable<typeof tag> => tag !== null).map(tag => ({ _id: tag._id, name: tag.name })) || [],
                  app: ref.app ? { _id: ref.app._id, name: ref.app.name } : undefined,
                  author: ref.author ? {
                    _id: ref.author._id,
                    userId: ref.author.userId,
                    displayName: ref.author.displayName,
                    bio: ref.author.bio,
                    website: ref.author.website,
                    isPublicProfile: ref.author.isPublicProfile,
                    publicStats: ref.author.publicStats
                  } : undefined
                }))}
                viewMode={viewMode}
                isLoading={publicReferences === undefined}
              />

              {/* Load More */}
              {publicReferences?.hasMore && (
                <div className="text-center mt-8">
                  <button type="button" className="btn btn-primary btn-lg shadow-comic">
                    Load More References
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}